import { ApiClient } from '@api/api-configuration';
import { captureError } from '@app/lib/Sentry';
import { normalizeLanguageCode, setAppLanguage } from '@app/i18n/helpers';
import AccessDenied from '@pages/AccessDenied';
import { parseJwt } from '@feat-auth/auth.config';
import { userManager } from '@app/features/oidc/oidc.config';
import { REDIRECT_AFTER_LOGIN_KEY } from '@app/features/oidc/redirectAfterLogin';
import { APP_ROLES, canAccessPortal, hasAnyAppRole, isDoctorPersona } from '@feat-auth/roles';
import { ParsedKeycloakToken } from '@feat-auth/types';
import { deactivatePushToken } from '@app/features/web-push/deactivatePushToken';
import { clearGlobalSearchPrefs, pruneOtherUsersRecent } from '@app/features/globalSearch/prefs';
import { getDisplayTimezone, subscribeDisplayTimezone } from '@app/utils/dates/displayTimezone';
import { createProfileTimezoneSync } from '@app/features/timezone/profileTimezoneSync';
import { Fragment, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { AuthContext, defaultUser, UserContextType } from './AuthContext';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const keycloakAuth = useAuth();

  const [isInitialized, setIsInitialized] = useState(false);
  const [user, setUser] = useState<UserContextType>(defaultUser);
  const [accessDenied, setAccessDenied] = useState(false);
  // Keeps users.timezone on the browser zone; the user id is read at save time.
  const userIdRef = useRef(-1);
  const [zoneSync] = useState(() =>
    createProfileTimezoneSync((timezone) => ApiClient.usersApi.users.updateUser(userIdRef.current, { timezone }))
  );
  const [displayTimezone, setDisplayTimezoneState] = useState(getDisplayTimezone);

  useEffect(() => subscribeDisplayTimezone(setDisplayTimezoneState), []);

  const accessToken = keycloakAuth.user?.access_token || null;

  // The account's saved language is applied once, when the session starts. Re-applying it
  // later would fight the user: the access token is refreshed periodically (silent renew),
  // and every refresh used to re-run this effect and push the server's copy of the locale
  // back over whatever language the user had picked since — which read as the language
  // switch silently undoing itself.
  const hasAppliedSavedLocale = useRef(false);

  useEffect(() => {
    if (!accessToken) return;

    try {
      // Must go through parseJwt: the payload is base64url, which raw atob() rejects
      // outright ('-'/'_' are not valid base64), and it is UTF-8, which atob() mangles.
      const payload = parseJwt<ParsedKeycloakToken>(accessToken);
      const roles = payload.realm_access?.roles ?? [];

      // A user must be provisioned with at least one recognized application role
      // to access the system. Keycloak assigns default roles (offline_access,
      // uma_authorization, default-roles-*) to every account, so those don't
      // count — we only look for the app's own roles. No app role means the
      // account isn't set up for this system, so deny access.
      if (!hasAnyAppRole(roles)) {
        setAccessDenied(true);
        return;
      }

      // The portal is for staff accounts. Admins ('adm'), office admins ('ofa')
      // and doctors ('doc') are allowed in. A caregiver ('cgv') without any portal
      // role is denied — but a caregiver who ALSO holds one keeps access.
      const hasPortalRole = canAccessPortal(roles);
      if (roles.includes(APP_ROLES.CAREGIVER) && !hasPortalRole) {
        setAccessDenied(true);
        return;
      }
      setAccessDenied(false);
      setIsInitialized(true);

      const numericUserId = Number(payload.attributes.userId) || -1;

      setUser({
        email: payload.email,
        name: payload.name,
        fullname: payload.name || 'N/A',
        userId: numericUserId,
        // TODO : Replace acronyms with full words to be descriptive. Also hardcoded should come from consts ex : USER_ROLES.ADMIN
        isAdmin: roles.includes(APP_ROLES.ADMIN),
        isCaregiver: roles.includes(APP_ROLES.CAREGIVER),
        isOfficeAdmin: roles.includes(APP_ROLES.OFFICE_ADMIN),
        // Which portal to show, not which role is held: this flag hides the whole
        // staff portal, so an administrator who is also a doctor keeps the staff
        // one and only a caregiver-or-nothing doctor gets the doctor's.
        isDoctor: isDoctorPersona(roles),
      });

      if (numericUserId > 0 && !hasAppliedSavedLocale.current) {
        hasAppliedSavedLocale.current = true;

        void ApiClient.usersApi.users
          .getUserById(numericUserId)
          .then((res) => {
            const savedLocale = (res.data as { locale?: string | null }).locale;
            if (savedLocale) void setAppLanguage(normalizeLanguageCode(savedLocale));

            userIdRef.current = numericUserId;
            void zoneSync.loaded(res.data.timezone);
          })
          .catch(() => undefined);
      }
    } catch (e) {
      captureError(e instanceof Error ? e : new Error('Failed to parse JWT'), {
        accessToken: accessToken?.substring(0, 20) + '...',
      });
    }
    // keycloakAuth is deliberately not a dependency: nothing in here reads it, but its
    // identity changes on every OIDC state update, which re-ran the whole effect (and the
    // locale fetch above) far more often than the token actually changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // A session that ended without a logout (closed browser, failed renew) leaves its recent results.
  useEffect(() => {
    if (user.userId > 0) pruneOtherUsersRecent(user.userId);
  }, [user.userId]);

  // Persist the user's chosen language to their account (best-effort; the UI
  // language is already applied locally by the caller).
  const persistLocale = useCallback(
    (locale: string) => {
      if (user.userId > 0) {
        void ApiClient.usersApi.users.updateUser(user.userId, { locale }).catch(() => undefined);
      }
    },
    [user.userId]
  );

  // A tab left open while travelling: re-check the zone whenever it comes back into view.
  useEffect(() => {
    if (user.userId <= 0) return undefined;
    // focus and visibilitychange both fire on return; the sync runs one save at a time.
    const recheck = () => {
      if (document.visibilityState !== 'hidden') void zoneSync.sync();
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [user.userId, zoneSync]);

  const logout = () => {
    // Hand the push token back first, so notifications for this user stop
    // reaching a browser nobody is signed into. Awaited rather than fired and
    // forgotten because `signoutRedirect` navigates away and would cancel the
    // request; it is bounded and never rejects, so the redirect always follows.
    void deactivatePushToken().finally(async () => {
      clearGlobalSearchPrefs();
      // Revoke the access and refresh tokens before ending the session. Best effort and
      // bounded: a failed or slow revoke must never keep the user signed in.
      await Promise.race([
        userManager.revokeTokens().catch(() => undefined),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
      keycloakAuth.signoutRedirect();
      setTimeout(() => sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY), 0);
    });
  };

  if (accessDenied) {
    return <AccessDenied onSignOut={logout} />;
  }

  return (
    <AuthContext.Provider
      value={{
        auth: accessToken,
        removeAuthentication: keycloakAuth.removeUser,
        logout,
        isInitialized,
        user,
        persistLocale,
      }}
    >
      {/* Remount on a zone change so every already-formatted date re-renders in the new zone. */}
      <Fragment key={displayTimezone}>{children}</Fragment>
    </AuthContext.Provider>
  );
};
